import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * HeroFX — multi-layer cinematic FX system for the AG Public Adjusting Hero.
 * Renders, per active scene:
 *   • Background image with per-scene camera-motion preset
 *   • Mid-ground service FX (rain, ripple, ember, scan, flashlight)
 *   • Foreground depth layer (lens droplets / smoke wisps / dust / glass)
 *   • Anamorphic horizontal lens streak
 *   • Lens flare bloom
 *   • Edge vignette + film grain + subtle scan lines
 *   • Per-scene transition overlay (storm flash / smoke wipe / water ripple /
 *     gold sweep / blueprint scan / lens flare / glass distort)
 *
 * Everything is CSS keyframes + small DOM, GPU-friendly.
 */

// ---- foreground particle generators ----
const seededPositions = (count, seed) => {
  const out = [];
  let s = seed;
  for (let i = 0; i < count; i++) {
    s = (s * 9301 + 49297) % 233280;
    const left = (s / 233280) * 100;
    s = (s * 9301 + 49297) % 233280;
    const top = (s / 233280) * 100;
    s = (s * 9301 + 49297) % 233280;
    const delay = (s / 233280) * 6;
    s = (s * 9301 + 49297) % 233280;
    const dur = 3 + (s / 233280) * 5;
    out.push({ left, top, delay, dur });
  }
  return out;
};

const ForegroundRainDroplets = () => {
  const drops = useMemo(() => seededPositions(28, 17), []);
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      {drops.map((d) => (
        <span
          key={`drop-${d.left}-${d.top}-${d.delay}`}
          className="fg-droplet"
          style={{
            left: `${d.left}%`,
            top: `${d.top}%`,
            animationDelay: `${d.delay}s`,
            animationDuration: `${d.dur + 4}s`,
          }}
        />
      ))}
    </div>
  );
};

const ForegroundSmokeWisps = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
    <span className="fg-smoke fg-smoke-1" />
    <span className="fg-smoke fg-smoke-2" />
    <span className="fg-smoke fg-smoke-3" />
  </div>
);

const ForegroundDust = () => {
  const motes = useMemo(() => seededPositions(48, 91), []);
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      {motes.map((m) => (
        <span
          key={`mote-${m.left}-${m.top}-${m.delay}`}
          className="fg-mote"
          style={{
            left: `${m.left}%`,
            top: `${m.top}%`,
            animationDelay: `${m.delay}s`,
            animationDuration: `${m.dur + 6}s`,
          }}
        />
      ))}
    </div>
  );
};

const ForegroundEmbers = () => {
  const sparks = useMemo(() => seededPositions(40, 41), []);
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      {sparks.map((s) => (
        <span
          key={`ember-${s.left}-${s.top}-${s.delay}`}
          className="fg-ember"
          style={{
            left: `${s.left}%`,
            top: `${70 + (s.top % 30)}%`,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.dur + 3}s`,
          }}
        />
      ))}
    </div>
  );
};

const ForegroundForFx = ({ fx }) => {
  if (fx === "rain") return <ForegroundRainDroplets />;
  if (fx === "ember") return <ForegroundEmbers />;
  if (fx === "flashlight") return <ForegroundDust />;
  if (fx === "ripple") return <ForegroundDust />;
  if (fx === "scan") return <ForegroundDust />;
  return null;
};

// ---- per-scene transition overlays ----
const TransitionOverlay = ({ kind, activeKey }) => {
  return (
    <AnimatePresence>
      <motion.div
        key={`tr-${activeKey}`}
        className="absolute inset-0 pointer-events-none z-30"
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        aria-hidden="true"
      >
        {kind === "stormFlash" && <div className="tr-storm-flash" />}
        {kind === "smokeWipe" && <div className="tr-smoke-wipe" />}
        {kind === "waterRipple" && <div className="tr-water-ripple" />}
        {kind === "goldSweep" && <div className="tr-gold-sweep" />}
        {kind === "blueprintScan" && <div className="tr-blueprint-scan" />}
        {kind === "lensFlare" && <div className="tr-lens-flare" />}
        {kind === "glassDistort" && <div className="tr-glass-distort" />}
      </motion.div>
    </AnimatePresence>
  );
};

export default function HeroFX({ scene, activeKey, sceneMs = 6500 }) {
  return (
    <>
      {/* Background image with per-scene camera motion */}
      <AnimatePresence mode="sync">
        <motion.div
          key={`bg-${activeKey}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 z-0"
        >
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${scene.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "brightness(0.45) contrast(1.12) saturate(0.9)",
              animation: `cam-${scene.camera} ${sceneMs + 1800}ms cubic-bezier(0.22,1,0.36,1) forwards`,
              willChange: "transform",
            }}
          />
          {/* mid-ground service FX */}
          <div className={`fx-${scene.fx} absolute inset-0 pointer-events-none mix-blend-screen opacity-85 z-[1]`} aria-hidden="true" />
          {/* per-scene accent ambient pulse */}
          <div
            className="absolute inset-0 pointer-events-none z-[2]"
            style={{
              background:
                scene.accent === "gold"
                  ? "radial-gradient(ellipse 55% 45% at 30% 60%, rgba(198,168,107,0.20) 0%, transparent 60%)"
                  : "radial-gradient(ellipse 55% 45% at 70% 30%, rgba(0,170,255,0.22) 0%, transparent 60%)",
              animation: `accentPulse ${sceneMs}ms ease-in-out forwards`,
            }}
          />
          {/* foreground depth layer (close-to-lens particles) */}
          <div className="absolute inset-0 z-[3] pointer-events-none">
            <ForegroundForFx fx={scene.fx} />
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Anamorphic horizontal lens streak — premium cinema feel */}
      <div
        className="absolute inset-x-0 top-[28%] h-[2px] pointer-events-none z-[4]"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, rgba(120,180,255,0) 20%, rgba(120,180,255,0.5) 50%, rgba(120,180,255,0) 80%, transparent 100%)",
          filter: "blur(2px)",
          animation: "anamorphDrift 14s ease-in-out infinite alternate",
          willChange: "transform, opacity",
        }}
        aria-hidden="true"
      />

      {/* Lens flare — soft bloom */}
      <div
        className="absolute pointer-events-none z-[4]"
        style={{
          top: "12%",
          right: "8%",
          width: "320px",
          height: "320px",
          background: "radial-gradient(circle, rgba(120,180,255,0.18) 0%, transparent 60%)",
          filter: "blur(8px)",
          animation: "flareBloom 9s ease-in-out infinite alternate",
        }}
        aria-hidden="true"
      />

      {/* Edge vignette */}
      <div
        className="absolute inset-0 pointer-events-none z-[5]"
        style={{
          boxShadow: "inset 0 0 220px 60px rgba(0,8,28,0.85)",
        }}
        aria-hidden="true"
      />

      {/* Subtle film grain (CSS-only) */}
      <div className="absolute inset-0 pointer-events-none z-[5] opacity-[0.06] grain-overlay" aria-hidden="true" />

      {/* Transition overlay — per-scene style */}
      <TransitionOverlay kind={scene.transition} activeKey={activeKey} />

      {/* ===== keyframes & class styles ===== */}
      <style>{`
        @keyframes accentPulse { 0% { opacity: 0; } 30% { opacity: 1; } 100% { opacity: 0.55; } }

        /* CAMERA MOTION PRESETS ------------------------------------- */
        @keyframes cam-pushIn       { 0% { transform: scale(1.02) translate(0,0); } 100% { transform: scale(1.18) translate(0,0); } }
        @keyframes cam-pushOut      { 0% { transform: scale(1.18) translate(0,0); } 100% { transform: scale(1.04) translate(0,0); } }
        @keyframes cam-driftLeft    { 0% { transform: scale(1.10) translate(2%, 0); } 100% { transform: scale(1.10) translate(-2%, 0); } }
        @keyframes cam-driftRight   { 0% { transform: scale(1.10) translate(-2%, 0); } 100% { transform: scale(1.10) translate(2%, 0); } }
        @keyframes cam-driftDiagTL  { 0% { transform: scale(1.06) translate(2%, 2%); } 100% { transform: scale(1.16) translate(-2%, -2%); } }
        @keyframes cam-driftDiagBR  { 0% { transform: scale(1.06) translate(-2%, -2%); } 100% { transform: scale(1.16) translate(2%, 2%); } }
        @keyframes cam-parallaxDown { 0% { transform: scale(1.06) translateY(-2%); } 100% { transform: scale(1.14) translateY(2%); } }
        @keyframes cam-orbitR       { 0% { transform: scale(1.08) rotate(-0.6deg); } 100% { transform: scale(1.14) rotate(0.6deg); } }

        /* MID-GROUND SERVICE FX ------------------------------------- */
        .fx-rain { background-image: repeating-linear-gradient(105deg, transparent 0, transparent 5px, rgba(180,210,255,0.12) 6px, transparent 8px); animation: rainFall 0.9s linear infinite; }
        .fx-rain::after { content:""; position:absolute; inset:0; background: radial-gradient(ellipse at 50% 30%, rgba(220,240,255,0) 0%, transparent 70%); animation: lightningFlash 7s ease-in-out infinite; }
        @keyframes rainFall { 0% { background-position: 0 0; } 100% { background-position: -22px 64px; } }
        @keyframes lightningFlash { 0%, 92%, 100% { background: radial-gradient(ellipse at 50% 30%, rgba(220,240,255,0) 0%, transparent 70%); } 93% { background: radial-gradient(ellipse at 50% 30%, rgba(220,240,255,0.55) 0%, transparent 70%); } 94% { background: radial-gradient(ellipse at 50% 30%, rgba(220,240,255,0) 0%, transparent 70%); } 95% { background: radial-gradient(ellipse at 50% 30%, rgba(220,240,255,0.35) 0%, transparent 70%); } 96% { background: radial-gradient(ellipse at 50% 30%, rgba(220,240,255,0) 0%, transparent 70%); } }

        .fx-ripple::before { content:""; position:absolute; inset:0; background: radial-gradient(circle at 30% 40%, rgba(0,170,255,0.20), transparent 50%), radial-gradient(circle at 70% 70%, rgba(0,170,255,0.16), transparent 55%); animation: rippleShift 7s ease-in-out infinite alternate; }
        .fx-ripple::after { content:""; position:absolute; left:50%; top:55%; width:60vw; height:60vw; transform: translate(-50%,-50%); border: 1px solid rgba(0,170,255,0.22); border-radius: 50%; animation: rippleExpand 4s ease-out infinite; }
        @keyframes rippleShift  { 0% { transform: translate(0,0); } 100% { transform: translate(2%,2%); } }
        @keyframes rippleExpand { 0% { transform: translate(-50%,-50%) scale(0.4); opacity: 0.85; } 100% { transform: translate(-50%,-50%) scale(1.2); opacity: 0; } }

        .fx-ember { background: radial-gradient(circle at 50% 100%, rgba(255,150,80,0.22), transparent 55%); }
        .fx-ember::before { content:""; position:absolute; inset:0; background-image: radial-gradient(circle at 20% 80%, rgba(255,180,90,0.7) 1px, transparent 2px), radial-gradient(circle at 70% 70%, rgba(255,200,120,0.55) 1px, transparent 2px), radial-gradient(circle at 45% 90%, rgba(255,160,80,0.65) 1px, transparent 2px), radial-gradient(circle at 85% 85%, rgba(255,210,140,0.55) 1px, transparent 2px); animation: emberRise 6s linear infinite; }
        .fx-ember::after { content:""; position:absolute; inset:0; background: linear-gradient(0deg, rgba(255,140,60,0.08) 0%, transparent 50%); animation: heatShimmer 3s ease-in-out infinite alternate; }
        @keyframes emberRise   { 0% { transform: translateY(0); opacity: 0; } 30% { opacity: 1; } 100% { transform: translateY(-280px); opacity: 0; } }
        @keyframes heatShimmer { 0% { transform: skewX(0deg); } 100% { transform: skewX(0.5deg); } }

        .fx-scan::before { content:""; position:absolute; inset:0; background: linear-gradient(180deg, transparent 0, transparent 48%, rgba(0,170,255,0.35) 50%, transparent 52%, transparent 100%); animation: scanSweep 4.5s ease-in-out infinite; }
        .fx-scan::after { content:""; position:absolute; inset:0; background-image: linear-gradient(rgba(0,170,255,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(0,170,255,0.07) 1px, transparent 1px); background-size: 80px 80px; animation: gridDrift 14s linear infinite; opacity: 0.55; }
        @keyframes scanSweep { 0% { transform: translateY(-100%); } 100% { transform: translateY(100%); } }
        @keyframes gridDrift { 0% { background-position: 0 0; } 100% { background-position: 80px 80px; } }

        .fx-flashlight::before { content:""; position:absolute; inset:0; background: radial-gradient(circle at 30% 50%, rgba(255,235,200,0.25) 0%, transparent 26%); animation: flashlightMove 9s ease-in-out infinite alternate; }
        .fx-flashlight::after { content:""; position:absolute; inset:0; background-image: radial-gradient(circle at 25% 45%, rgba(255,255,255,0.45) 0.5px, transparent 1.5px), radial-gradient(circle at 35% 60%, rgba(255,255,255,0.4) 0.5px, transparent 1.5px); animation: dustDrift 9s linear infinite; opacity: 0.75; }
        @keyframes flashlightMove { 0% { background-position: 20% 30%; } 100% { background-position: 70% 70%; } }
        @keyframes dustDrift { 0% { transform: translate(0,0); } 100% { transform: translate(-18px, -12px); } }

        /* FOREGROUND DEPTH LAYERS ----------------------------------- */
        .fg-droplet { position: absolute; width: 12px; height: 16px; border-radius: 50% 50% 60% 60% / 60% 60% 40% 40%; background: radial-gradient(ellipse at 35% 30%, rgba(255,255,255,0.55), rgba(180,210,255,0.18) 60%, transparent 100%); filter: blur(0.4px); opacity: 0; animation: dropletPulse 7s ease-in-out infinite; }
        @keyframes dropletPulse { 0%, 100% { opacity: 0; transform: translateY(0) scale(1); } 40% { opacity: 0.7; transform: translateY(2px) scale(1.06); } 80% { opacity: 0.25; transform: translateY(6px) scale(1); } }

        .fg-smoke { position: absolute; border-radius: 50%; filter: blur(48px); mix-blend-mode: screen; pointer-events: none; }
        .fg-smoke-1 { width: 60vw; height: 38vh; top: 22%; left: -25%; background: radial-gradient(circle, rgba(255,255,255,0.10), transparent 60%); animation: smokeDrift1 22s linear infinite; }
        .fg-smoke-2 { width: 45vw; height: 30vh; top: 50%; left: -35%; background: radial-gradient(circle, rgba(220,230,255,0.08), transparent 60%); animation: smokeDrift2 28s linear infinite; }
        .fg-smoke-3 { width: 70vw; height: 50vh; top: 0%; left: 10%; background: radial-gradient(circle, rgba(180,200,240,0.06), transparent 65%); animation: smokeDrift3 34s linear infinite; }
        @keyframes smokeDrift1 { 0% { transform: translateX(0); }   100% { transform: translateX(180vw); } }
        @keyframes smokeDrift2 { 0% { transform: translateX(0); }   100% { transform: translateX(180vw); } }
        @keyframes smokeDrift3 { 0% { transform: translateX(0) translateY(0); } 100% { transform: translateX(-60vw) translateY(8vh); } }

        .fg-mote { position: absolute; width: 2px; height: 2px; border-radius: 50%; background: rgba(255,255,255,0.6); box-shadow: 0 0 6px rgba(255,255,255,0.4); animation: moteFloat 9s ease-in-out infinite; opacity: 0; filter: blur(0.4px); }
        @keyframes moteFloat { 0% { opacity: 0; transform: translate(0,0) scale(0.8); } 25% { opacity: 0.9; transform: translate(8px,-12px) scale(1.1); } 60% { opacity: 0.65; transform: translate(-6px,-22px) scale(1); } 100% { opacity: 0; transform: translate(4px,-38px) scale(0.7); } }

        .fg-ember { position: absolute; width: 3px; height: 3px; border-radius: 50%; background: rgba(255,170,80,0.95); box-shadow: 0 0 10px rgba(255,150,60,0.8), 0 0 24px rgba(255,120,40,0.4); animation: emberFloat 7s ease-out infinite; opacity: 0; }
        @keyframes emberFloat { 0% { opacity: 0; transform: translate(0,0) scale(0.8); } 20% { opacity: 1; } 100% { opacity: 0; transform: translate(20px, -260px) scale(0.4); } }

        /* ANAMORPHIC LENS STREAK + FLARE ---------------------------- */
        @keyframes anamorphDrift { 0% { opacity: 0.5; transform: translateX(-2%); } 100% { opacity: 0.85; transform: translateX(2%); } }
        @keyframes flareBloom    { 0% { opacity: 0.55; transform: scale(0.9); } 100% { opacity: 0.95; transform: scale(1.08); } }

        /* TRANSITION OVERLAYS --------------------------------------- */
        .tr-storm-flash    { position:absolute; inset:0; background: radial-gradient(ellipse at 50% 30%, rgba(255,255,255,0.85), transparent 70%); animation: stormFlashAnim 1.4s ease-out forwards; }
        @keyframes stormFlashAnim { 0% { opacity: 0; } 12% { opacity: 1; } 14% { opacity: 0.2; } 20% { opacity: 0.9; } 30% { opacity: 0; } 100% { opacity: 0; } }

        .tr-smoke-wipe     { position:absolute; inset:0; background: linear-gradient(95deg, rgba(20,20,30,0.0) 0%, rgba(40,40,50,0.85) 40%, rgba(60,60,70,0.85) 50%, rgba(40,40,50,0.85) 60%, transparent 100%); filter: blur(40px); animation: smokeWipeAnim 1.4s ease-out forwards; }
        @keyframes smokeWipeAnim { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }

        .tr-water-ripple   { position:absolute; inset:0; }
        .tr-water-ripple::before { content:""; position:absolute; left:50%; top:50%; width: 10vw; height: 10vw; border:2px solid rgba(0,170,255,0.7); border-radius: 50%; transform: translate(-50%,-50%); animation: rippleBurst 1.4s ease-out forwards; }
        .tr-water-ripple::after  { content:""; position:absolute; left:50%; top:50%; width: 5vw; height: 5vw; border:2px solid rgba(120,200,255,0.85); border-radius: 50%; transform: translate(-50%,-50%); animation: rippleBurst 1.4s ease-out 0.2s forwards; }
        @keyframes rippleBurst { 0% { transform: translate(-50%,-50%) scale(0.4); opacity: 1; } 100% { transform: translate(-50%,-50%) scale(20); opacity: 0; } }

        .tr-gold-sweep     { position:absolute; inset:0; background: linear-gradient(95deg, transparent 0%, rgba(0,170,255,0.2) 35%, rgba(255,235,200,0.3) 50%, rgba(198,168,107,0.25) 65%, transparent 100%); filter: blur(60px); mix-blend-mode: screen; animation: goldSweepAnim 1.4s ease-out forwards; }
        @keyframes goldSweepAnim { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }

        .tr-blueprint-scan { position:absolute; inset:0; background: linear-gradient(180deg, transparent 0, transparent 40%, rgba(0,170,255,0.7) 50%, transparent 60%, transparent 100%); animation: blueprintScanAnim 1.4s ease-out forwards; }
        .tr-blueprint-scan::after { content:""; position:absolute; inset:0; background-image: linear-gradient(rgba(0,170,255,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(0,170,255,0.18) 1px, transparent 1px); background-size: 60px 60px; opacity: 0; animation: blueprintGridFade 1.4s ease-out forwards; }
        @keyframes blueprintScanAnim { 0% { transform: translateY(-100%); } 100% { transform: translateY(100%); } }
        @keyframes blueprintGridFade { 0% { opacity: 0; } 40% { opacity: 0.85; } 100% { opacity: 0; } }

        .tr-lens-flare     { position:absolute; inset:0; }
        .tr-lens-flare::before { content:""; position:absolute; left:-10%; top:50%; right:-10%; height: 4px; background: linear-gradient(90deg, transparent 0%, rgba(120,180,255,0.95) 50%, transparent 100%); filter: blur(6px); transform: translateY(-50%); animation: lensFlareAnim 1.4s ease-out forwards; }
        .tr-lens-flare::after { content:""; position:absolute; left:50%; top:50%; width: 600px; height: 600px; transform: translate(-50%,-50%); background: radial-gradient(circle, rgba(255,235,200,0.6) 0%, transparent 60%); filter: blur(20px); animation: lensFlareBloom 1.4s ease-out forwards; }
        @keyframes lensFlareAnim  { 0% { opacity: 0; transform: translateY(-50%) scaleX(0.4); } 30% { opacity: 1; transform: translateY(-50%) scaleX(1); } 100% { opacity: 0; transform: translateY(-50%) scaleX(1.4); } }
        @keyframes lensFlareBloom { 0% { opacity: 0; transform: translate(-50%,-50%) scale(0.4); } 35% { opacity: 0.95; transform: translate(-50%,-50%) scale(1.0); } 100% { opacity: 0; transform: translate(-50%,-50%) scale(1.4); } }

        .tr-glass-distort  { position:absolute; inset:0; backdrop-filter: blur(0px); animation: glassDistortAnim 1.4s ease-out forwards; background: linear-gradient(120deg, rgba(120,180,255,0.06), rgba(255,255,255,0.04)); }
        @keyframes glassDistortAnim { 0% { backdrop-filter: blur(0px); opacity: 0; } 30% { backdrop-filter: blur(10px); opacity: 1; } 100% { backdrop-filter: blur(0px); opacity: 0; } }
      `}</style>
    </>
  );
}
